package org.teddapp.project.ui.skills

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Attachment
import com.tedd.db.Skill_resource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.db.repo.SkillResourceRepository
import org.teddapp.project.resources.PickedFile
import org.teddapp.project.resources.ResourceRules
import org.teddapp.project.resources.ResourceRules.State
import org.teddapp.project.resources.TeddFilePicker
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerSession
import org.teddapp.project.sync.ServerIds
import org.teddapp.project.ui.TeddDates

/** Les deux appels au serveur dont l'écran a besoin ; remplacés par un faux dans les tests. */
internal interface ResourceServer {
    /** Relance l'analyse ; lève une [ServerException] si le serveur refuse. */
    suspend fun reindex(resourceId: String)

    /** Le nombre de ressources de la compétence qui fait foi. */
    suspend fun usage(skillId: String): Int
}

/** Le serveur de la session connectée. Les identifiants locaux courts y prennent leur UUID. */
internal class SessionResourceServer(private val session: ServerSession) : ResourceServer {
    private fun serverId(local: String) = if (ServerIds.isUuid(local)) local.lowercase() else ServerIds.derive(local)

    override suspend fun reindex(resourceId: String) {
        session.authed { api, access -> api.reindexResource(access, serverId(resourceId)) }
    }

    override suspend fun usage(skillId: String): Int =
        session.authed { api, access -> api.resourcesUsage(access, serverId(skillId)) }.resource_count
}

/** Comment on ajoute une ressource. */
internal enum class AddMode { Link, Text, File }

/** Une ressource telle que la liste la montre. */
internal class ResourceItem(
    val row: Skill_resource,
    val state: State,
    /** La phrase sous le titre : la raison d'un échec, la date d'une analyse. */
    val detail: String?,
) {
    val id: String get() = row.id
    val typeLabel: String get() = ResourceRules.formatLabel(row.origin, row.format)
    val stateLabel: String get() = ResourceRules.stateLabel(state)
    /** « Réessayer » après un échec, « Actualiser » pour un lien. */
    val retryLabel: String?
        get() = when {
            state == State.Failed -> "Réessayer"
            row.origin == "link" && (state == State.Ready || state == State.RefreshFailed) -> "Actualiser"
            else -> null
        }
}

/**
 * Les ressources d'une compétence : la liste, l'ajout d'un lien, d'un texte ou d'un
 * fichier, et les gestes sur chacune. Les données viennent de la base locale ; seuls
 * la réindexation et, près de la limite, le compte qui fait foi passent par le serveur.
 */
internal class ResourcesModel(
    private val repo: SkillResourceRepository,
    val skillId: String,
    private val scope: CoroutineScope,
    private val server: () -> ResourceServer?,
) {
    var resources by mutableStateOf<List<Skill_resource>>(emptyList())
    var trash by mutableStateOf<List<Skill_resource>>(emptyList())
    var files by mutableStateOf<List<Attachment>>(emptyList())

    /** Un message pour l'utilisateur, au-dessus de la liste. */
    var notice by mutableStateOf<String?>(null)

    // ── Ajout ──
    var adding by mutableStateOf<AddMode?>(null)
    var url by mutableStateOf("")
    var title by mutableStateOf("")
    var body by mutableStateOf("")
    var markdown by mutableStateOf(false)
    var picked by mutableStateOf<PickedFile?>(null)
    var formError by mutableStateOf<String?>(null)
    var busy by mutableStateOf(false)
    /** Le compte du serveur, lu à l'ouverture de l'ajout quand on approche la limite. */
    var serverCount by mutableStateOf<Int?>(null)
    private var titleEdited = false
    private var formatChosen = false

    // ── Renommage ──
    var renaming by mutableStateOf<Skill_resource?>(null)
    var renameText by mutableStateOf("")
    var renameError by mutableStateOf<String?>(null)

    val canPickFiles: Boolean get() = TeddFilePicker.pick != null

    /** Ressources vivantes et acceptées : celles qui comptent dans la limite. */
    val localCount: Int get() = resources.count { it.push_error == null }

    /** « 42 / 300 », ou le compte du serveur s'il a été lu et dépasse le local. */
    val counter: String get() = "${maxOf(localCount, serverCount ?: 0)} / ${ResourceRules.MAX_PER_SKILL}"

    val items: List<ResourceItem>
        get() {
            val uploaded = files.associate { it.owner_id to (it.status == "uploaded") }
            return resources.map { r ->
                val state = ResourceRules.stateOf(r.origin, r.index_status, r.refresh_error, r.push_error, uploaded[r.id] ?: false)
                ResourceItem(r, state, detailOf(r, state))
            }
        }

    private fun detailOf(r: Skill_resource, state: State): String? = when (state) {
        State.Refused -> ResourceRules.refusalMessage(r.push_error)
        State.Failed -> ResourceRules.indexErrorMessage(r.index_error)
        State.RefreshFailed -> "La page n'a pas pu être relue. L'assistant garde la version du ${day(r.indexed_at) ?: "dernier passage réussi"}."
        State.Ready -> day(r.indexed_at)?.let { "Analysée le $it." }
        State.Uploading -> "Le fichier part dès que l'appareil est connecté au serveur."
        State.Pending, State.Indexing -> null
    }

    private fun day(ms: Long?): String? = ms?.let { TeddDates.formatShort(TeddDates.isoFromDays((it / 86_400_000L).toInt()), withYear = true) }

    fun openAdd(mode: AddMode) {
        adding = mode
        url = ""; title = ""; body = ""; markdown = false; picked = null
        formError = null; titleEdited = false; formatChosen = false
        if (localCount >= ResourceRules.MAX_PER_SKILL) formError = ResourceRules.refusalMessage("quota_exceeded")
        // Le compte local peut être en retard sur un autre appareil : près de la limite,
        // on demande au serveur, une seule fois, à l'ouverture.
        if (localCount > ResourceRules.CHECK_SERVER_ABOVE) {
            val s = server() ?: return
            scope.launch {
                val count = runCatching { s.usage(skillId) }.getOrNull() ?: return@launch
                serverCount = count
                if (count >= ResourceRules.MAX_PER_SKILL) formError = ResourceRules.refusalMessage("quota_exceeded")
            }
        }
    }

    fun closeAdd() {
        adding = null
        formError = null
    }

    fun onUrl(value: String) {
        url = value
        if (!titleEdited) title = ResourceRules.titleForLink(value)
    }

    fun onBody(value: String) {
        body = value
        if (!titleEdited) title = ResourceRules.titleForText(value)
        if (!formatChosen) markdown = ResourceRules.looksLikeMarkdown(value)
    }

    /** Texte simple ou Markdown, choisi par l'utilisateur : la détection s'arrête. */
    fun chooseMarkdown(value: Boolean) {
        markdown = value
        formatChosen = true
    }

    fun onTitle(value: String) {
        title = value
        titleEdited = true
    }

    /** Ouvre le sélecteur de fichiers ; le fichier choisi est contrôlé tout de suite. */
    fun pickFile() {
        val pick = TeddFilePicker.pick ?: return
        scope.launch {
            val file = runCatching { pick(ResourceRules.FILE_EXTENSIONS) }.getOrNull() ?: return@launch
            choose(file)
        }
    }

    fun choose(file: PickedFile) {
        picked = file
        if (!titleEdited) title = ResourceRules.titleForFile(file.name)
        formError = ResourceRules.fileProblem(file.name, file.bytes)
    }

    /** Ajoute la ressource du formulaire ; ferme le formulaire si c'est fait. */
    fun submit() {
        val mode = adding ?: return
        if (busy) return
        busy = true
        scope.launch {
            val result = try {
                when (mode) {
                    AddMode.Link -> repo.addLink(skillId, url, title)
                    AddMode.Text -> repo.addText(skillId, title, body, markdown)
                    AddMode.File -> picked?.let { repo.addFile(skillId, title, it) }
                        ?: SkillResourceRepository.Added.Problem("Choisis un fichier.")
                }
            } finally {
                busy = false
            }
            when (result) {
                is SkillResourceRepository.Added.Done -> {
                    adding = null
                    notice = when (mode) {
                        AddMode.File -> "« ${title.trim()} » est ajoutée. Le fichier part au serveur, puis l'assistant l'analyse."
                        else -> "« ${title.trim()} » est ajoutée. L'assistant l'analysera sous peu."
                    }
                }
                is SkillResourceRepository.Added.Problem -> formError = result.message
            }
        }
    }

    fun startRename(r: Skill_resource) {
        renaming = r
        renameText = r.title
        renameError = null
    }

    fun confirmRename() {
        val r = renaming ?: return
        scope.launch {
            val problem = repo.rename(r.id, renameText)
            if (problem == null) renaming = null else renameError = problem
        }
    }

    fun moveUp(r: Skill_resource) = neighbour(r, -1)

    fun moveDown(r: Skill_resource) = neighbour(r, +1)

    private fun neighbour(r: Skill_resource, step: Int) {
        val list = resources
        val i = list.indexOfFirst { it.id == r.id }
        val j = i + step
        if (i < 0 || j !in list.indices) return
        scope.launch { repo.swap(list[i], list[j]) }
    }

    fun delete(r: Skill_resource) {
        scope.launch {
            repo.delete(r.id)
            notice = if (r.origin == "file") {
                "« ${r.title} » est supprimée. L'espace de son fichier sera libéré dans ${ResourceRules.PURGE_DAYS} jours. Tu peux la restaurer depuis « Récemment supprimées »."
            } else {
                "« ${r.title} » est supprimée. Tu peux la restaurer depuis « Récemment supprimées »."
            }
        }
    }

    fun restore(r: Skill_resource) {
        if (localCount >= ResourceRules.MAX_PER_SKILL) {
            notice = ResourceRules.refusalMessage("quota_exceeded")
            return
        }
        scope.launch {
            repo.restore(r.id)
            notice = "« ${r.title} » est restaurée. L'assistant l'analysera de nouveau."
        }
    }

    /** « Réessayer » ou « Actualiser » : la même route du serveur. */
    fun reindex(r: Skill_resource) {
        val s = server()
        if (s == null) {
            notice = "Connecte-toi au serveur pour relancer l'analyse."
            return
        }
        scope.launch {
            notice = try {
                s.reindex(r.id)
                "L'analyse de « ${r.title} » est relancée."
            } catch (e: ServerException) {
                ResourceRules.reindexMessage(e.code)
            } catch (e: kotlinx.coroutines.CancellationException) {
                throw e
            } catch (_: Throwable) {
                "Le serveur ne répond pas. Réessaie quand l'appareil est en ligne."
            }
        }
    }
}

/** Le modèle des ressources de la compétence [skillId], branché sur la base locale. */
@Composable
internal fun rememberResourcesModel(repo: SkillResourceRepository, skillId: String, session: ServerSession?): ResourcesModel {
    val scope = rememberCoroutineScope()
    val model = remember(repo, skillId) {
        ResourcesModel(repo, skillId, scope) { session?.takeIf { it.connected }?.let(::SessionResourceServer) }
    }
    model.resources = remember(repo, skillId) { repo.observeBySkill(skillId) }.collectAsState(emptyList()).value
    model.trash = remember(skillId) { repo.observeTrash(skillId) }.collectAsState(emptyList()).value
    model.files = remember(repo) { repo.observeFiles() }.collectAsState(emptyList()).value
    LaunchedEffect(model.notice) {
        if (model.notice != null) {
            kotlinx.coroutines.delay(8_000)
            model.notice = null
        }
    }
    return model
}
